import { useEffect, useState } from 'react';
import { AlertTriangle, KeyRound, Lock, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  fetchSiteMqttAuth,
  setSiteMqttCreds,
  clearSiteMqttCreds,
  setSiteMqttEnforce,
  type SiteMqttAuthStatus,
} from '@/features/sites/api';
import { showToast } from '@/lib/toast';

/**
 * Per-site MQTT authentication (super_admin). Opt-in: a site is anonymous until
 * credentials are set AND enforcement is turned on. Enforcing before boards hold
 * the credentials would lock them out — the UI warns about the safe rollout order.
 */
export function MqttSecuritySection({ siteId, siteCode }: { siteId: number; siteCode: string }) {
  const [status, setStatus] = useState<SiteMqttAuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    fetchSiteMqttAuth(siteId)
      .then(setStatus)
      .catch((e) => showToast((e as Error).message ?? 'โหลดสถานะ MQTT ไม่สำเร็จ', 'error'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [siteId]);

  const mode = !status?.username ? 'anonymous' : status.enabled ? 'enforced' : 'set';

  const startEdit = () => {
    setUsername(status?.username ?? '');
    setPassword('');
    setEditing(true);
  };

  const saveCreds = async () => {
    if (!username.trim() || !password) {
      showToast('กรอก username และ password', 'error');
      return;
    }
    setBusy(true);
    try {
      setStatus(await setSiteMqttCreds(siteId, username.trim(), password));
      setEditing(false);
      setPassword('');
      showToast('บันทึก credential MQTT แล้ว', 'success');
    } catch (e) {
      showToast((e as Error).message ?? 'บันทึกไม่สำเร็จ', 'error');
    } finally {
      setBusy(false);
    }
  };

  const clearCreds = async () => {
    if (!confirm(`ล้าง credential MQTT ของไซต์ ${siteCode}? การบังคับใช้จะถูกปิดด้วย`)) return;
    setBusy(true);
    try {
      setStatus(await clearSiteMqttCreds(siteId));
      showToast('ล้าง credential แล้ว', 'info');
    } catch (e) {
      showToast((e as Error).message ?? 'ล้างไม่สำเร็จ', 'error');
    } finally {
      setBusy(false);
    }
  };

  const toggleEnforce = async () => {
    const next = !status?.enabled;
    if (next) {
      const ok = confirm(
        `⚠️ บังคับใช้ MQTT auth ของไซต์ ${siteCode}?\n\n` +
          'บอร์ดที่ยังไม่มี credential นี้จะเชื่อมต่อไม่ได้ทันที (หยุดส่งข้อมูล)\n' +
          'ให้แน่ใจว่า push credential ลงบอร์ดทุกตัวในไซต์ก่อนแล้ว',
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      setStatus(await setSiteMqttEnforce(siteId, next));
      showToast(next ? 'บังคับใช้ MQTT auth แล้ว' : 'ปิดการบังคับใช้แล้ว', next ? 'success' : 'info');
    } catch (e) {
      showToast((e as Error).message ?? 'เปลี่ยนสถานะไม่สำเร็จ', 'error');
    } finally {
      setBusy(false);
    }
  };

  const badge =
    mode === 'enforced'
      ? { icon: <ShieldCheck size={13} />, label: 'บังคับใช้อยู่', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', bd: 'rgba(34,197,94,0.35)' }
      : mode === 'set'
        ? { icon: <KeyRound size={13} />, label: 'ตั้ง cred แล้ว (ยังไม่บังคับ)', color: '#facc15', bg: 'rgba(250,204,21,0.12)', bd: 'rgba(250,204,21,0.35)' }
        : { icon: <ShieldOff size={13} />, label: 'anonymous (เปิดให้ทุกคน)', color: 'var(--dim)', bg: 'var(--bg-input)', bd: 'var(--border-color)' };

  return (
    <section className="card" style={{ padding: 18, marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
          <Lock size={16} /> ความปลอดภัย MQTT
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999, color: badge.color, background: badge.bg, border: `1px solid ${badge.bd}` }}>
          {badge.icon} {badge.label}
        </span>
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--dim)', margin: '0 0 12px' }}>
        ตั้ง user/pass เพื่อบังคับเฉพาะไซต์นี้ · ถ้าไม่ตั้ง บอร์ดต่อแบบ anonymous ได้เหมือนเดิม
      </p>

      {loading ? (
        <div style={{ color: 'var(--dim)', fontSize: 13 }}>กำลังโหลด...</div>
      ) : editing ? (
        <div style={{ display: 'grid', gap: 10, maxWidth: 420 }}>
          <div>
            <label style={lbl}>Username</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} maxLength={32} style={inp} />
          </div>
          <div>
            <label style={lbl}>Password</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} maxLength={64} style={{ ...inp, flex: 1 }} placeholder="พิมพ์รหัสผ่านใหม่" />
              <button type="button" onClick={() => setShowPw((v) => !v)} style={btnGhost}>{showPw ? 'ซ่อน' : 'แสดง'}</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={saveCreds} disabled={busy} style={btnPrimary}>บันทึก</button>
            <button onClick={() => { setEditing(false); setPassword(''); }} disabled={busy} style={btnGhost}>ยกเลิก</button>
          </div>
        </div>
      ) : (
        <>
          <div className="table-wrap" style={{ marginBottom: 12 }}>
            <table style={{ width: '100%', fontSize: 13 }}>
              <tbody>
                <tr><td style={{ color: 'var(--dim)', padding: '4px 0', width: 130 }}>Username</td><td style={{ color: 'var(--text)' }}>{status?.username ?? '—'}</td></tr>
                <tr><td style={{ color: 'var(--dim)', padding: '4px 0' }}>Password</td><td style={{ color: 'var(--text)' }}>{status?.hasPassword ? '●●●●●● (ตั้งแล้ว)' : '—'}</td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={startEdit} disabled={busy} style={btnPrimary}>{status?.username ? 'แก้ไข cred' : 'ตั้ง user/pass'}</button>
            {status?.username && <button onClick={clearCreds} disabled={busy} style={btnDanger}>ล้าง cred</button>}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto', fontSize: 13, color: status?.hasPassword ? 'var(--text)' : 'var(--dim2)', cursor: status?.hasPassword ? 'pointer' : 'not-allowed' }} title={status?.hasPassword ? '' : 'ต้องตั้ง cred ก่อน'}>
              <input type="checkbox" checked={!!status?.enabled} disabled={!status?.hasPassword || busy} onChange={toggleEnforce} />
              บังคับใช้ auth
            </label>
          </div>
        </>
      )}

      {/* Rollout warning */}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, padding: '10px 12px', borderRadius: 8, background: 'rgba(250,204,21,0.06)', border: '1px solid rgba(250,204,21,0.2)', fontSize: 12, color: 'var(--text)' }}>
        <AlertTriangle size={15} color="var(--yellow)" style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          <strong>ลำดับที่ปลอดภัย:</strong> ตั้ง cred → <strong>push cred ลงบอร์ดทุกตัวในไซต์ก่อน</strong> → ยืนยันบอร์ดรับครบ → ค่อยกด "บังคับใช้"
          — ถ้าบังคับก่อนบอร์ดได้ cred บอร์ดจะโดนล็อกเอาต์ทันที
        </span>
      </div>
    </section>
  );
}

const lbl: React.CSSProperties = { display: 'block', fontSize: 11, color: 'var(--dim)', fontWeight: 600, marginBottom: 4 };
const inp: React.CSSProperties = { background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text)', padding: '8px 10px', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', width: '100%' };
const btnBase: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: '1px solid var(--border-color)' };
const btnPrimary: React.CSSProperties = { ...btnBase, background: 'var(--cyan)', color: '#000', border: '1px solid var(--cyan)' };
const btnGhost: React.CSSProperties = { ...btnBase, background: 'var(--bg-input)', color: 'var(--text)' };
const btnDanger: React.CSSProperties = { ...btnBase, background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.35)' };
